import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams, useRouterState, useSearch } from "@tanstack/react-router";
import type { Player } from "@backstory/player";
import { AssetResolver, qualityBucket } from "@backstory/player";
import type { FlatEvent, Issue, VisitSurface } from "@backstory/protocol";
import { API_URL, api, queryKeys, request, type ExportJob, type SessionShareLink } from "../api";
import { useCan } from "../auth/roles";
import { EventList } from "../components/EventList";
import { PlayerView } from "../components/PlayerView";
import { SurfacePane } from "../components/SurfacePane";
import { TimelineLane } from "../components/TimelineLane";
import { VisitSurfaceRail } from "../components/VisitSurfaceRail";
import { isOnScreen, momentOn, nextTrigger, pickPanes, resolveMoment, stateChanges, statusesAt, surfaceOf, visitBounds, visitSurfaces, type StageMode, type SurfaceStateChange } from "../components/surfaces";
import { EmptySessionsMark, ErrorState, Skeleton } from "../components/States";
import { ErrorsBadge, GapsIcon, LevelBadge } from "../components/Badges";
import { ConfirmDialog, CopyButton, Dialog, Field, RowMenu, useToast } from "../components/ui";
import { DevToolsDock, DEFAULT_DOCK_WIDTH, readDockTab, SessionInformationPanel, usePanelActions, usePanelFetchers, type DockTab } from "../features/devtools";
import { callsApi, callKeys } from "../features/calls/api";
import { AskWhyButton, AskWhyDrawer, SummaryCard, WhyPanel, useCitationSeek } from "../features/ai";
import { formatLocation } from "../lib/country";
import { formatClock, formatDateTime, formatDuration } from "../lib/format";
import { buildBackstorySession, downloadJson } from "../lib/backstorysession";

/** Absolute timestamps of trigger-worthy events per session (errors, failed requests, custom triggers). */
export function triggerTimes(events: FlatEvent[]): number[] {
  return events.filter((e) => e.type === "error" || (e.type === "network" && (e.status ?? 0) >= 500) || (e.type === "custom" && /^rw\.|trigger|alarm|lost/i.test(e.name ?? ""))).map((e) => e.ts);
}

const enc = encodeURIComponent;
const LS_STAGE_MODE = "backstory-stage-mode";
const LS_WORKSPACE_MODE = "backstory-session-workspace";
type WorkspaceMode = "replay" | "split" | "devtools";

function readStageMode(): StageMode {
  try {
    return localStorage.getItem(LS_STAGE_MODE) === "single" ? "single" : "auto";
  } catch {
    return "auto";
  }
}

function readWorkspaceMode(): WorkspaceMode {
  try {
    const value = localStorage.getItem(LS_WORKSPACE_MODE);
    return value === "replay" || value === "devtools" ? value : "split";
  } catch {
    return "split";
  }
}

export function SessionPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const search = useSearch({ strict: false }) as { t?: number | string; at?: number | string };
  const navigate = useNavigate();
  const back = useRouterState({
    select: (s) => {
      const state = s.location.state as {
        sessionsSearch?: Record<string, unknown>;
        sessionReturn?: { to: "/live/history" | "/staff/ai-feedback"; label: string };
      };
      return state.sessionReturn ?? { to: "/" as const, label: "Sessions", search: state.sessionsSearch ?? {} };
    },
  });
  const toast = useToast();
  const queryClient = useQueryClient();
  const [activityOpen, setActivityOpen] = useState(false);
  const session = useQuery({ queryKey: queryKeys.session(id), queryFn: () => api.session(id) });
  const events = useQuery({ queryKey: queryKeys.events(id), queryFn: () => api.events(id), enabled: session.isSuccess });
  const visit = useQuery({ queryKey: queryKeys.visit(session.data?.visitId ?? ""), queryFn: () => api.visit(session.data!.visitId), enabled: !!session.data?.visitId });
  const issues = useQuery({
    queryKey: ["issues", id],
    queryFn: () => request<Issue[] | { items: Issue[] }>(`/v1/sessions/${enc(id)}/issues`).then((r) => (Array.isArray(r) ? r : r.items ?? [])),
    enabled: session.isSuccess,
    retry: false,
  });
  const assets = useQuery({
    queryKey: ["assets", id],
    queryFn: () => request<unknown>(`/v1/sessions/${enc(id)}/assets`),
    enabled: session.isSuccess,
    retry: false,
    staleTime: Infinity,
  });
  const calls = useQuery({
    queryKey: callKeys.forSession(id),
    queryFn: () => callsApi.forSession(id),
    enabled: session.isSuccess,
    retry: false,
  });
  const activity = useQuery({
    queryKey: queryKeys.sessionActivity(id),
    queryFn: () => api.sessionActivity(id),
    enabled: activityOpen,
  });
  const [time, setTime] = useState(0);
  const [version, setVersion] = useState(0);
  const [player, setPlayer] = useState<Player | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareHours, setShareHours] = useState(72);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<SessionShareLink | null>(null);
  const closeShare = useCallback(() => {
    setShareOpen(false);
    setShareUrl(null);
  }, []);
  const shares = useQuery({
    queryKey: queryKeys.sessionShares(id),
    queryFn: () => api.shares(id),
    enabled: shareOpen,
  });
  /* Links you can still revoke come first, newest first within each group: the
     live ones are what the reader came to check, and expired or revoked ones are
     history that belongs below them. */
  const shareLinks = useMemo(() => {
    const now = Date.now();
    return (shares.data?.items ?? [])
      .map((link) => ({ link, active: !link.revokedAt && new Date(link.expiresAt).getTime() > now }))
      .sort((a, b) =>
        a.active === b.active
          ? new Date(b.link.createdAt).getTime() - new Date(a.link.createdAt).getTime()
          : Number(b.active) - Number(a.active),
      );
  }, [shares.data]);
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  /** The dock's width sizes its grid track, which is clamped so it cannot squeeze out the replay. */
  const [dockWidth, setDockWidth] = useState(DEFAULT_DOCK_WIDTH);
  const [dockTab, setDockTab] = useState<DockTab>(readDockTab);
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>(readWorkspaceMode);
  const can = useCan();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const removeSession = useMutation({
    mutationFn: () => api.deleteSession(id),
    onSuccess: () => {
      setDeleteOpen(false);
      toast.push("info", "Session deleted.");
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      void navigate({ to: "/sessions" });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const refs = useMemo(() => session.data?.chunks ?? [], [session.data?.chunks]);
  const fetchChunk = useCallback((seq: number) => api.chunk(id, seq), [id]);
  const startTs = session.data ? new Date(session.data.startedAt).getTime() : 0;

  // ---- Surfaces of this visit, on one shared clock -------------------------
  const surfaces = useMemo(() => (visit.data ? visitSurfaces(visit.data) : []), [visit.data]);
  const bounds = useMemo(() => (visit.data ? visitBounds(visit.data) : { start: startTs, end: startTs + 1 }), [visit.data, startTs]);
  const routed = useMemo(() => surfaceOf(surfaces, id), [surfaces, id]);
  const [mode, setMode] = useState<StageMode>(readStageMode);
  const [pinned, setPinned] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  /** The visit clock: one absolute moment every surface resolves against. */
  const nowAbs = startTs + time;

  useEffect(() => {
    try {
      localStorage.setItem(LS_STAGE_MODE, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);
  useEffect(() => {
    try {
      localStorage.setItem(LS_WORKSPACE_MODE, workspaceMode);
    } catch {
      /* ignore */
    }
  }, [workspaceMode]);

  // Surface state (on screen, focused) comes from each surface's segment that
  // covers the current moment, so following costs one events query per surface.
  const stateSegmentIds = useMemo(() => surfaces.map((sf) => momentOn(sf, nowAbs).segment?.id ?? ""), [surfaces, nowAbs]);
  const stateQueries = useQueries({
    queries: stateSegmentIds.map((sid) => ({
      queryKey: queryKeys.events(sid),
      queryFn: () => api.events(sid),
      enabled: !!sid && surfaces.length > 1,
      staleTime: 60_000,
    })),
  });
  const statesBySurface = useMemo(() => {
    const out: Record<number, SurfaceStateChange[]> = {};
    surfaces.forEach((sf, i) => {
      const items = stateSegmentIds[i] === id ? events.data?.items : stateQueries[i]?.data?.items;
      out[sf.surfaceId] = stateChanges(items ?? []);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfaces, stateSegmentIds, events.data, stateQueries.map((q) => q.dataUpdatedAt).join(","), id]);

  // Errors and alarms of every surface we have events for, so the rail marks
  // them on the right track and "next trigger" can cross surfaces.
  const triggers = useMemo(() => {
    const out: Record<string, number[]> = { [id]: triggerTimes(events.data?.items ?? []) };
    stateSegmentIds.forEach((sid, i) => {
      const items = sid && sid !== id ? stateQueries[i]?.data?.items : undefined;
      if (items) out[sid] = triggerTimes(items);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.data, id, stateSegmentIds, stateQueries.map((q) => q.dataUpdatedAt).join(",")]);

  const statuses = useMemo(() => statusesAt(surfaces, nowAbs, statesBySurface), [surfaces, nowAbs, statesBySurface]);
  const statusOf = useCallback((sf: VisitSurface) => statuses[sf.surfaceId] ?? "gap", [statuses]);
  const upcoming = useMemo(() => nextTrigger(visit.data?.segments ?? [], triggers, nowAbs), [visit.data, triggers, nowAbs]);
  const panes = useMemo(() => (routed ? pickPanes(surfaces, statusOf, { mode, routed, pinned }) : null), [surfaces, statusOf, mode, routed, pinned]);

  /** Play a known segment at a moment on the visit clock. */
  const goToSegment = useCallback(
    (sessionId: string, absTs: number) => {
      if (sessionId === id) {
        void playerRef.current?.seek(Math.max(0, absTs - startTs));
        return;
      }
      const dest = visit.data?.segments.find((s) => s.id === sessionId);
      if (!dest) return;
      const rel = Math.max(0, absTs - new Date(dest.startedAt).getTime());
      // A number, not a string: the router serializes search values as JSON, so
      // a string would arrive quoted and parse back as NaN.
      void navigate({ to: "/sessions/$id", params: { id: dest.id }, search: { t: Math.round(rel) } as never, state: true });
    },
    [id, startTs, visit.data, navigate],
  );

  /** Move the visit clock, switching surface or segment when the moment needs it. */
  const goToAbs = useCallback(
    (absTs: number, surface?: VisitSurface) => {
      // Without an explicit surface the moment may belong to another one, e.g.
      // a citation pointing at a time this tab was not recording.
      const m = resolveMoment(surfaces, surface ?? routed, absTs, { anySurface: !surface });
      if (!m?.segment) {
        void playerRef.current?.seek(Math.max(0, absTs - startTs));
        return;
      }
      // Outside any recording, the nearest segment plays from the offset it
      // offered rather than from a moment it does not contain.
      goToSegment(m.segment.id, m.covered ? absTs : new Date(m.segment.startedAt).getTime() + m.offsetMs);
    },
    [routed, surfaces, startTs, goToSegment],
  );

  /**
   * Seeking is visit-aware: a citation or event whose time falls outside this
   * segment lands on the segment that actually recorded it.
   */
  const seek = useCallback(
    (rel: number) => {
      const p = playerRef.current;
      if (p && rel >= 0 && rel <= p.duration + 1) {
        void p.seek(rel);
        return;
      }
      goToAbs(startTs + rel);
    },
    [goToAbs, startTs],
  );

  // Auto-follow: when the surface on the stage goes off screen and another one
  // has focus, playback follows the user to it, keeping the same moment.
  // Remembering the hand-off we just made, not merely its destination, keeps a
  // route change from re-triggering while still allowing a later follow back.
  const lastAuto = useRef("");
  useEffect(() => {
    if (mode !== "auto" || !routed || pinned != null || surfaces.length < 2) return;
    if (isOnScreen(statusOf(routed))) return;
    const focused = surfaces.find((sf) => statusOf(sf) === "focused");
    if (!focused || focused.surfaceId === routed.surfaceId) return;
    const seg = momentOn(focused, nowAbs).segment;
    if (!seg || seg.id === id) return;
    const hop = `${id}->${seg.id}`;
    if (lastAuto.current === hop) return;
    lastAuto.current = hop;
    goToAbs(nowAbs, focused);
  }, [mode, routed, pinned, surfaces, statusOf, nowAbs, id, goToAbs]);

  // Asset archive: rewrite recorded URLs to archived copies when the map is available.
  const resolver = useMemo(() => {
    if (!assets.data) return null;
    const r = new AssetResolver({ assetBase: `${API_URL}/v1/assets` });
    try {
      r.load(assets.data as never);
    } catch {
      return null;
    }
    return r.size ? r : null;
  }, [assets.data]);
  useEffect(() => {
    if (player && resolver) player.setAssetRewriter(resolver.rewriter);
  }, [player, resolver]);

  // Deep link ?t=<ms>. Applied per segment, because moving to another surface
  // routes to a different segment with the offset that keeps the same moment.
  // ?at=<epoch ms> is the same link for callers that only know the wall clock
  // (a call, an alert) and not where the session started.
  const startTsRef = useRef(startTs);
  useEffect(() => {
    startTsRef.current = startTs;
  }, [startTs]);
  const deepLinkMs = useCallback((): number | null => {
    if (search.t !== undefined && search.t !== "") return Number(search.t);
    if (search.at !== undefined && search.at !== "") return Math.max(0, Number(search.at) - startTsRef.current);
    return null;
  }, [search.t, search.at]);
  const seekedFor = useRef<string | null>(null);
  const onReady = useCallback(
    (p: Player) => {
      playerRef.current = p;
      setPlayer(p);
      p.on("chunkloaded", () => setVersion((v) => v + 1));
      p.on("loaded", () => setVersion((v) => v + 1));
      // A synchronized surface mirrors this transport rather than owning one.
      p.on("statechange", ({ state }) => setPlaying(state === "playing"));
      if (seekedFor.current !== id && (search.t || search.at)) {
        seekedFor.current = id;
        const off = p.on("loaded", () => {
          // Resolved here, not at mount: ?at needs the session's start time,
          // which the detail query may still have been loading.
          const t = deepLinkMs();
          if (t !== null && Number.isFinite(t)) void p.seek(t);
          off();
        });
      }
    },
    [search.t, search.at, deepLinkMs, id],
  );

  // Keep the visit clock honest the instant the route changes: the new
  // segment's player has not reported a time yet, and 0 would jump the clock.
  useEffect(() => {
    setTime(Number(search.t) || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const panelActions = usePanelActions(playerRef, startTs);
  const panelFetchers = usePanelFetchers(id);
  const cite = useCitationSeek(id, seek, startTs);

  const share = useMutation({
    mutationFn: () => api.share(id, shareHours, Math.round(time)),
    onSuccess: (r) => {
      setShareUrl(r.url);
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessionShares(id) });
    },
    onError: (e) => toast.push("error", `Could not create the public link: ${(e as Error).message}`),
  });
  const revokeShare = useMutation({
    mutationFn: (linkId: string) => api.revokeShare(id, linkId),
    onSuccess: () => {
      toast.push("success", "Public link revoked");
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessionShares(id) });
    },
    onError: (e) => toast.push("error", `Could not revoke the public link: ${(e as Error).message}`),
  });
  const review = useMutation({
    mutationFn: (reviewed: boolean) => api.reviewSession(id, reviewed),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.session(id), (current: typeof session.data) =>
        current ? { ...current, reviewedAt: result.reviewedAt, reviewedBy: result.reviewedBy } : current,
      );
      void queryClient.invalidateQueries({ queryKey: ["sessions"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessionActivity(id) });
      toast.push("success", result.reviewed ? "Session marked reviewed." : "Session marked not reviewed.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const startExport = useMutation({
    mutationFn: () => api.exportStart(id),
    onSuccess: (j) => setExportJob(j),
    onError: (e) => toast.push("error", `Could not start the MP4 export: ${(e as Error).message}`),
  });
  useEffect(() => {
    if (!exportJob || exportJob.status === "ready" || exportJob.status === "failed") return;
    const h = setInterval(() => api.exportStatus(id, exportJob.id).then(setExportJob).catch(() => undefined), 3000);
    return () => clearInterval(h);
  }, [exportJob, id]);

  const download = async () => {
    if (!session.data) return;
    try {
      const file = await buildBackstorySession(session.data, fetchChunk, (d, t) => setDownloading(`${d}/${t}`));
      downloadJson(`${id}.backstorysession`, file);
      toast.push("success", "Session archive downloaded.");
    } catch (e) {
      toast.push("error", (e as Error).message);
    } finally {
      setDownloading(null);
    }
  };

  const badges = useMemo(() => {
    const ev = events.data?.items ?? [];
    const consoleErrors = ev.filter((e) => e.type === "error" || (e.type === "console" && e.subtype === "error")).length;
    const failed = ev.filter((e) => e.type === "network" && (e.status ?? 0) >= 400).length;
    const callProblems = (calls.data ?? []).reduce((n, c) => n + c.freezeCount + c.mediaErrors.length + (c.failedToConnect ? 1 : 0), 0);
    return { console: consoleErrors || undefined, network: failed || undefined, media: callProblems || calls.data?.length || undefined, issues: issues.data?.length || undefined };
  }, [events.data, issues.data, calls.data]);
  const callHealth = useMemo(() => {
    const list = calls.data ?? [];
    if (!list.length) return null;
    const freezes = list.reduce((n, c) => n + c.freezeCount, 0);
    const freezeMs = list.reduce((n, c) => n + c.freezeMsTotal, 0);
    const failed = list.filter((c) => c.failedToConnect).length;
    const errors = list.reduce((n, c) => n + c.mediaErrors.length, 0);
    const scores = list.map((c) => c.qualityScore).filter((n): n is number => typeof n === "number");
    const quality = scores.length ? Math.min(...scores) : undefined;
    return { calls: list.length, freezes, freezeMs, failed, errors, quality, bucket: qualityBucket(quality) };
  }, [calls.data]);

  if (session.isPending) return <Skeleton lines={8} />;
  if (session.isError) return <ErrorState error={session.error} retry={() => void session.refetch()} />;
  const s = session.data;
  const localShareUrl = `${window.location.origin}/sessions/${id}?t=${Math.round(time)}`;

  return (
    <div className={`session session--devtools session--workspace-${workspaceMode}`} style={{ ["--devtools-w" as string]: `${dockWidth}px` }}>
      <header className="session-header">
        {back.to === "/live/history" ? (
          <Link to="/live/history" className="btn ghost session-back">
            <span aria-hidden="true">←</span> <span className="session-back-label">{back.label}</span>
          </Link>
        ) : back.to === "/staff/ai-feedback" ? (
          <Link to="/staff/ai-feedback" className="btn ghost session-back">
            <span aria-hidden="true">←</span> <span className="session-back-label">{back.label}</span>
          </Link>
        ) : (
          <Link to="/" search={("search" in back ? back.search : {}) as never} className="btn ghost session-back">
            <span aria-hidden="true">←</span> <span className="session-back-label">{back.label}</span>
          </Link>
        )}
        <div className="session-heading">
          <h1>{s.title || s.url}</h1>
          <div className="meta">
            <span>{formatDateTime(s.startedAt)}</span>
            <span>· {formatDuration(s.durationMs)}</span>
            {s.reviewedAt ? (
              <button type="button" className="badge ok" onClick={() => setActivityOpen(true)} title={`Reviewed ${formatDateTime(s.reviewedAt)}`}>✓ Reviewed</button>
            ) : s.lastViewedAt ? (
              <button type="button" className="badge neutral" onClick={() => setActivityOpen(true)} title={`Last viewed ${formatDateTime(s.lastViewedAt)}`}>Viewed</button>
            ) : <span className="badge info">New</span>}
          {/* The zero and dash placeholders these render are there to keep the
              sessions table's columns aligned; in a heading they are just noise. */}
          {s.errorCount ? <ErrorsBadge count={s.errorCount} /> : null}
          <LevelBadge level={s.level} />
          {s.hasGaps ? <GapsIcon hasGaps /> : null}
          </div>
        </div>
        <span className="spacer" />
        <div className="session-workspace-switch" role="group" aria-label="Workspace layout">
          {(["replay", "split", "devtools"] as const).map((option) => (
            <button type="button" key={option} aria-pressed={workspaceMode === option} onClick={() => setWorkspaceMode(option)}>
              {option === "replay" ? "Replay" : option === "split" ? "Split" : "DevTools"}
            </button>
          ))}
        </div>
        <div className="actions-row">
          {can.write && (
            <button
              className={s.reviewedAt ? "btn" : "btn primary"}
              aria-pressed={!!s.reviewedAt}
              onClick={() => review.mutate(!s.reviewedAt)}
              disabled={review.isPending}
            >
              {review.isPending ? "Saving…" : s.reviewedAt ? "Mark not reviewed" : "Mark reviewed"}
            </button>
          )}
          <RowMenu label="More session actions">
            <button role="menuitem" onClick={() => setActivityOpen(true)}>Audit history</button>
            <button role="menuitem" onClick={() => { setDockTab("info"); setWorkspaceMode("devtools"); }}>Session info</button>
            <Link role="menuitem" to="/visits/$id" params={{ id: s.visitId }}>Open visit</Link>
            {callHealth ? <button role="menuitem" onClick={() => { setDockTab("media"); setWorkspaceMode("devtools"); }}>WebRTC diagnostics</button> : null}
            {can.write && <button role="menuitem" onClick={() => { setShareUrl(null); setShareOpen(true); }}>Share</button>}
            <button role="menuitem" onClick={() => void download()} disabled={!!downloading}>{downloading ? `Downloading ${downloading}` : "Download archive"}</button>
            {can.write && (
              <button role="menuitem" onClick={() => startExport.mutate()} disabled={startExport.isPending || (!!exportJob && exportJob.status !== "ready" && exportJob.status !== "failed")}>
                {exportJob && exportJob.status !== "ready" && exportJob.status !== "failed" ? `Exporting video (${exportJob.status})` : "Export video"}
              </button>
            )}
            {exportJob?.status === "ready" && exportJob.downloadUrl ? <a role="menuitem" href={exportJob.downloadUrl}>Download video</a> : null}
            {can.admin && <button role="menuitem" className="danger" onClick={() => setDeleteOpen(true)}>Delete session</button>}
          </RowMenu>
        </div>
      </header>

      <div className="player-col">
        {panes && (surfaces.length > 1 || (visit.data?.segments?.length ?? 0) > 1) && (
          <VisitSurfaceRail
            surfaces={surfaces}
            nowAbs={nowAbs}
            visitStart={bounds.start}
            visitEnd={bounds.end}
            statusOf={statusOf}
            primaryId={panes.primary.surfaceId}
            secondaryId={panes.secondary?.surfaceId}
            pinnedId={pinned}
            mode={mode}
            onModeChange={setMode}
            onSelect={(sf) => goToAbs(nowAbs, sf)}
            onPin={setPinned}
            triggers={triggers}
            onJumpTo={(sf, ts) => goToAbs(ts, sf)}
            hasNextTrigger={!!upcoming}
            onNextTrigger={() => upcoming && goToSegment(upcoming.sessionId, upcoming.ts)}
          />
        )}
        <div className="stage" data-panes={panes?.secondary ? 2 : 1}>
          <PlayerView refs={refs} fetchChunk={fetchChunk} onTime={setTime} onReady={onReady} privacyMode={s.privacyMode} sessionKey={id} label={surfaces.length > 1 ? `Surface ${s.surfaceId + 1} · segment ${s.segmentIndex}` : undefined} />
          {panes?.secondary && (
            <SurfacePane
              surface={panes.secondary}
              nowAbs={nowAbs}
              playing={playing}
              states={statesBySurface[panes.secondary.surfaceId]}
              status={statuses[panes.secondary.surfaceId]}
              onJumpTo={(ts) => goToAbs(ts, panes.secondary)}
              onPromote={() => goToAbs(nowAbs, panes.secondary)}
            />
          )}
        </div>
        {/* Below the stage: the timeline keeps its height and the summary takes
            what is left, scrolling its prose inside its own frame. One scroller
            around both carried the timeline off the screen. */}
        <div className="player-tail">
          <TimelineLane session={s} events={events.data?.items ?? []} issues={issues.data ?? undefined} time={time} onSeek={seek} />
          <SummaryCard sessionId={id} tools={cite} compact />
        </div>
      </div>

      <DevToolsDock
        player={player}
        time={time}
        version={version}
        actions={panelActions}
        fetchers={panelFetchers}
        onWidthChange={setDockWidth}
        fullWidth={workspaceMode === "devtools"}
        badges={badges}
        whyPanel={<WhyPanel sessionId={id} player={player} time={time} startTs={startTs} seek={seek} issues={issues.data ?? undefined} />}
        infoPanel={<SessionInformationPanel session={s} events={events.data?.items ?? []} />}
        location={formatLocation(s) || undefined}
        activeTab={dockTab}
        onTabChange={setDockTab}
        eventsPanel={
          events.isError ? (
            <ErrorState error={events.error} retry={() => void events.refetch()} />
          ) : (
            <EventList events={events.data?.items ?? []} startTs={startTs} time={time} onSeek={seek} />
          )
        }
      />

      <AskWhyDrawer seek={seek} />
      <AskWhyButton context={{ sessionId: id, label: `session ${id.slice(0, 8)}…` }} />

      <Dialog open={activityOpen} onClose={() => setActivityOpen(false)} title="Audit History" fullScreen footer={<button className="btn" onClick={() => setActivityOpen(false)}>Done</button>}>
        <p className="muted small">Opening the replay records a view. Review status changes only when someone marks it reviewed or not reviewed.</p>
        {activity.isPending ? (
          <Skeleton lines={4} />
        ) : activity.isError ? (
          <ErrorState error={activity.error} retry={() => void activity.refetch()} />
        ) : activity.data?.items.length ? (
          <div className="session-activity-list">
            {activity.data.items.map((item) => {
              const who = item.actor.name || item.actor.email || item.actor.id;
              const action = item.action === "session.view" ? "Viewed" : item.action === "session.reviewed" ? "Marked reviewed" : "Marked not reviewed";
              return (
                <div className="session-activity-row" key={item.id}>
                  <span className={`badge ${item.action === "session.reviewed" ? "ok" : "neutral"}`}>{action}</span>
                  <span className="session-activity-who"><strong>{who}</strong>{item.actor.name && item.actor.email ? <small>{item.actor.email}</small> : null}</span>
                  <time className="muted small" dateTime={item.at} title={formatDateTime(item.at)}>{formatDateTime(item.at)}</time>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty"><EmptySessionsMark /><h3>No history yet</h3><p>This replay has not been opened.</p></div>
        )}
      </Dialog>

      <Dialog open={shareOpen} onClose={closeShare} title="Share session" width={860} className="share-dialog" footer={<button className="btn" onClick={closeShare}>Done</button>}>
        <section className="share-section">
          <div className="share-section-head">
            <div>
              <h3>Team link</h3>
              <p>Anyone on your team with session access can open this moment.</p>
            </div>
            <span className="badge neutral">Sign-in required</span>
          </div>
          <div className="share-link-box">
            <span className="mono">{localShareUrl}</span>
            <CopyButton value={localShareUrl} />
          </div>
        </section>

        <section className="share-section">
          <div className="share-create">
            <Field label="Expires after">
              {(p) => (
                <select {...p} className="input" value={shareHours} onChange={(e) => setShareHours(Number(e.target.value))}>
                  <option value={1}>1 hour</option>
                  <option value={24}>1 day</option>
                  <option value={72}>3 days</option>
                  <option value={168}>7 days</option>
                </select>
              )}
            </Field>
            <button className="btn primary" onClick={() => share.mutate()} disabled={share.isPending}>
              {share.isPending ? "Creating…" : "Create link"}
            </button>
          </div>
          {shareUrl && (
            <div className="share-created" data-testid="share-url" role="status">
              <div>
                <strong>Public link ready</strong>
                <span className="small muted">Copy it now. For security, the secret URL is not stored.</span>
              </div>
              <div className="share-link-box">
                <span className="mono">{shareUrl}</span>
                <CopyButton value={shareUrl} />
              </div>
            </div>
          )}

          <div className="share-list-head">
            <h4>Links for this session</h4>
            {shareLinks.length ? <span className="muted small">{shareLinks.filter((l) => l.active).length} active</span> : null}
          </div>
          {shares.isPending ? (
            <Skeleton lines={2} />
          ) : shares.isError ? (
            <ErrorState error={shares.error} retry={() => void shares.refetch()} />
          ) : shareLinks.length ? (
            <div className="share-list">
              {shareLinks.map(({ link, active }) => (
                <div className="share-row" key={link.id}>
                  <div className="share-row-main">
                    <span className={`badge ${active ? "ok" : "neutral"}`}>{link.revokedAt ? "Revoked" : active ? "Active" : "Expired"}</span>
                    <div>
                      <strong>{link.startMs > 0 ? `At ${formatClock(link.startMs)}` : "From the start"}</strong>
                      <span className="small muted">
                        {[link.createdByName ? `Made by ${link.createdByName}` : null, `Expires ${formatDateTime(link.expiresAt)}`, `Opened ${link.views} ${link.views === 1 ? "time" : "times"}`].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                  </div>
                  {active && can.write ? (
                    <RowMenu label={`Actions for the link expiring ${formatDateTime(link.expiresAt)}`}>
                      <button role="menuitem" className="danger" onClick={() => setPendingRevoke(link)}>Revoke link</button>
                    </RowMenu>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="share-empty">No public links have been created.</div>
          )}
        </section>
      </Dialog>
      <ConfirmDialog
        open={!!pendingRevoke}
        danger
        title="Revoke link"
        confirmLabel="Revoke link"
        busy={revokeShare.isPending}
        onClose={() => { if (!revokeShare.isPending) setPendingRevoke(null); }}
        onConfirm={() => {
          if (!pendingRevoke || revokeShare.isPending) return;
          revokeShare.mutate(pendingRevoke.id, { onSuccess: () => setPendingRevoke(null) });
        }}
      >
        <p style={{ margin: 0 }}>
          {pendingRevoke ? `The link expiring ${formatDateTime(pendingRevoke.expiresAt)} stops opening.` : ""}
        </p>
      </ConfirmDialog>
      <ConfirmDialog
        open={deleteOpen}
        danger
        title="Delete session"
        confirmLabel="Delete session"
        busy={removeSession.isPending}
        onClose={() => { if (!removeSession.isPending) setDeleteOpen(false); }}
        onConfirm={() => { if (!removeSession.isPending) removeSession.mutate(); }}
      >
        <p style={{ margin: 0 }}>Session {id} and everything recorded with it is removed. This includes the replay, events, and files.</p>
      </ConfirmDialog>
    </div>
  );
}
